import { Injectable, InjectionToken, NgZone, OnDestroy, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { Badge } from '@capawesome/capacitor-badge';
import { isNativeApp } from '../auth/native-auth';
import { MessageUnreadService } from '../../features/messages/shared/message-unread.service';

/**
 * DI token for the Capacitor `Badge` plugin. Injecting the plugin (instead of
 * calling the static proxy directly) keeps `AppBadgeService` unit-testable: the
 * real plugin proxy is provided in production while tests substitute a mock
 * (Capacitor plugin proxies can't be spied directly). Mirrors the
 * `PUSH_NOTIFICATIONS` token in native-push.service.ts.
 */
export const BADGE_PLUGIN = new InjectionToken<typeof Badge>(
  'BADGE_PLUGIN',
  { providedIn: 'root', factory: () => Badge },
);

/**
 * Reflects the caregiver's unread Message Center count onto the NATIVE app-icon
 * badge (Android launcher badge / iOS `applicationIconBadgeNumber`) via
 * `@capawesome/capacitor-badge`.
 *
 * Frontend half of rett-europe/rettx#28. The single source of truth is
 * {@link MessageUnreadService.unreadCount$} (made event-driven in PR #180):
 * every emission drives `Badge.set({ count })` when > 0 or `Badge.clear()` when
 * 0. Because #180 already refreshes the count on app foreground/resume, on
 * Message Center open, and optimistically on read/archive, the icon badge
 * tracks those events automatically — no second count mechanism is introduced.
 *
 * A push that arrives while the app is backgrounded or killed is badged by the
 * OS directly from the FCM payload (the rettxapi half of rett-europe/rettx#28);
 * on the next foreground the count refresh reconciles the badge to the true
 * value.
 *
 * Permission is NOT requested here: prompting at app startup would be wrong
 * (mirroring `NativePushService.init()`, which also never prompts). The OS
 * notification permission — which covers the badge — is requested only by the
 * user's explicit push opt-in (`NativePushService.enable()`). Until then
 * `Badge.set` simply no-ops on iOS, and the badge starts working once the user
 * opts in.
 *
 * The public API (`init`/`clear`) is a no-op on the web (guarded on
 * {@link isNativeApp}) and defensive throughout: any plugin error is swallowed
 * so a missing/broken badge implementation can never break app startup,
 * navigation, or logout. Android's numeric launcher badge is OEM/launcher-
 * dependent (many launchers show only a dot or nothing); iOS shows the number
 * reliably.
 */
@Injectable({ providedIn: 'root' })
export class AppBadgeService implements OnDestroy {
  private unreadService = inject(MessageUnreadService);
  private badge = inject(BADGE_PLUGIN);
  private zone = inject(NgZone);

  private initialized = false;
  private sub?: Subscription;

  /**
   * Start mirroring `unreadCount$` onto the app-icon badge. Native-only and
   * idempotent (only the first call on a native platform does work). Invoked
   * from `AppComponent.ngOnInit`, alongside the other native service inits.
   */
  init(): void {
    if (!isNativeApp() || this.initialized) {
      return;
    }
    this.initialized = true;

    this.sub = this.unreadService.unreadCount$.subscribe(count => {
      this.applyBadge(count);
    });
  }

  /**
   * Clear the app-icon badge (e.g. on logout / session clear) so a signed-out
   * device shows no count. Native-only and defensive.
   */
  async clear(): Promise<void> {
    if (!isNativeApp()) {
      return;
    }
    try {
      await this.badge.clear();
    } catch (err) {
      console.warn('[AppBadge] clear failed', err);
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.sub = undefined;
  }

  /**
   * Set the badge to `count` (when > 0) or clear it (when 0). Re-entered into
   * Angular's zone because `unreadCount$` can emit from a Capacitor callback
   * (e.g. the native `appStateChange` foreground refresh) that fires outside it
   * — mirrors the zone handling in `NativePushService`. Defensive: any plugin
   * error is swallowed.
   */
  private applyBadge(count: number): void {
    this.zone.run(() => {
      void this.updateBadge(count);
    });
  }

  private async updateBadge(count: number): Promise<void> {
    try {
      if (count > 0) {
        await this.badge.set({ count });
      } else {
        await this.badge.clear();
      }
    } catch (err) {
      console.warn('[AppBadge] failed to update app-icon badge', err);
    }
  }
}
